import { Scroll, type ScrollViewProps } from "@silk-hq/components";
import type { ReactNode, Ref } from "react";

export interface ScrollBoxProps {
  children: ReactNode;
  /**
   * Classes for the scroll container. Silk forces `display: grid` here, so
   * flex utilities do not apply — lay the content out with a wrapper inside.
   */
  className?: string;
  /** Classes for the content track inside the scroller. */
  contentClassName?: string;
  ref?: Ref<HTMLDivElement>;
  /** Removes the subtree from the tab order and the accessibility tree. */
  inert?: boolean;
  /** Gate the scroll gesture (e.g. a detent sheet that travels before it scrolls). */
  scrollGesture?: ScrollViewProps["scrollGesture"];
  scrollGestureTrap?: ScrollViewProps["scrollGestureTrap"];
  safeArea?: ScrollViewProps["safeArea"];
  onScrollStart?: ScrollViewProps["onScrollStart"];
}

/**
 * A Silk scroll container as one element.
 *
 * Silk's `Scroll` is a three-element anatomy whose parts each reject different
 * styles — the View forces `display: grid !important` and `padding: 0
 * !important`, the Root forces `display: grid` — so styling the wrong one
 * fails silently. Collapsing it here leaves one place to get that right, and
 * one addressable element for callers.
 *
 * Inside a Silk sheet this is what makes a single swipe hand off between sheet
 * travel and content scroll, and back again at scroll top.
 */
export function ScrollBox({
  children,
  className,
  contentClassName,
  ref,
  inert,
  scrollGesture,
  scrollGestureTrap,
  safeArea,
  onScrollStart,
}: ScrollBoxProps) {
  return (
    <Scroll.Root asChild className={className}>
      <Scroll.View
        ref={ref}
        inert={inert}
        onScrollStart={onScrollStart}
        safeArea={safeArea}
        scrollGesture={scrollGesture}
        scrollGestureTrap={scrollGestureTrap}
      >
        <Scroll.Content className={contentClassName}>{children}</Scroll.Content>
      </Scroll.View>
    </Scroll.Root>
  );
}
